/**
 * AN INVITATION THAT WAITS FOR HER CODE (found on staging, 2026-11-05; `docs/seam.md` P118). A
 * client holds a ninety-day sliding session, and only a code opens an invitation (CLAUDE.md binding
 * rule 3, `docs/identity.md` section 2, step 6), so an invitation ops sends her address or number
 * while she is signed in never came: the email's link opened the household she had, and the new one
 * waited until she signed out and in. Now /api/me says how many wait (`invitationsWaiting`), and
 * this says one does, above the page's content, with a button that runs the fresh-code step to the
 * address or number she signed in with (`FreshCode.tsx`), whose step-up batch on the Worker binds
 * every invitation for it, as a sign-in does, and tells ops she opened each.
 *
 * - NOTHING WHILE NONE WAITS, and no code is asked for until she presses the button, since the step
 *   asks for one as it is drawn and only the newest code works.
 * - BY CHANNEL: emailed to an address, "Email me a code"; texted to a number, "Text me a code", the
 *   step's words saying "text" where they say "email" (home #11). Signed in by a number where no
 *   code can be texted here, the step says why, and to call, and asks for none.
 * - EVERY OUTCOME SAID (the owner's word of 2026-10-20, never silent): the step says a code on its
 *   way, a code refused or not sent, a code that did not work and the guess lock, each as it says
 *   them in every other place. Once the code works, it says the invitation is open, in a status
 *   region that takes the focus, since the field goes with the step, and hands the page its reads
 *   again (`onOpened`): /api/me, which then counts none, and the place, which draws what the code
 *   opened by the place's own rules. The line stays said while the page is drawn.
 * - NOTHING OF THE HOUSEHOLD: /api/me says how many and nothing else, and home shows no household a
 *   code has not opened. Nothing here says the office has granted anything (binding rule 1).
 *
 * Drawn in a card as the code step an invoice's page asks for is (`screens/Invoice.tsx`), with the
 * card's outlined button.
 */
import { useEffect, useRef, useState } from 'react';
import type { SignedInWith } from '../api.ts';
import { INVITATION_WAITS } from '../words.ts';
import { CARD_BUTTON } from './buttons.ts';
import { FreshCode } from './FreshCode.tsx';

interface InvitationWaitsProps {
  /** The address or mobile number she signed in with, which the code goes to. */
  readonly signedInWith: SignedInWith;
  /** How many invitations wait for her code, as /api/me says it: nothing is drawn at none. */
  readonly waiting: number;
  /** The code worked: reads /api/me and the place again, so what it opened is drawn. */
  readonly onOpened: () => void;
  /** Where the notice stands in what draws it, such as the margin below it. */
  readonly className?: string;
}

/** Where the notice stands: saying one waits, asking for the code, or saying it is open. */
type Stage = 'waits' | 'code' | 'opened';

export function InvitationWaits({
  signedInWith,
  waiting,
  onOpened,
  className,
}: InvitationWaitsProps) {
  const [stage, setStage] = useState<Stage>('waits');
  const said = useRef<HTMLParagraphElement>(null);

  // The field went with the step, so the line that says the invitation is open takes the focus.
  useEffect(() => {
    if (stage === 'opened') said.current?.focus();
  }, [stage]);

  if (stage === 'waits' && waiting <= 0) return null;

  const byText = signedInWith.channel === 'text';
  const why = byText ? INVITATION_WAITS.texted : INVITATION_WAITS.emailed;

  return (
    <div
      data-invitation
      className={`rounded-card border border-border-subtle bg-surface-raised p-5${className === undefined ? '' : ` ${className}`}`}
    >
      {stage === 'waits' ? (
        <>
          <p className="text-body text-pretty text-text-body">{why}</p>
          <button
            type="button"
            onClick={() => setStage('code')}
            className={`mt-4 cursor-pointer ${CARD_BUTTON}`}
          >
            {byText ? INVITATION_WAITS.textMe : INVITATION_WAITS.emailMe}
          </button>
        </>
      ) : stage === 'code' ? (
        <FreshCode
          signedInWith={signedInWith}
          why={why}
          submit={INVITATION_WAITS.submit}
          closedBeforePhone={INVITATION_WAITS.cannotBeforePhone}
          onProved={async () => {
            setStage('opened');
            onOpened();
            return null;
          }}
        />
      ) : (
        <p ref={said} role="status" tabIndex={-1} className="text-body text-pretty text-text-body">
          {INVITATION_WAITS.opened}
        </p>
      )}
    </div>
  );
}
